import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  buttonStyles,
} from '@lognai/synapse-design-system-v2';

/**
 * Map this component's caller-facing variant names onto the design system's
 * Button variants. `default` and `link` have no Button equivalent, so both fall
 * back to the primary treatment.
 */
const BUTTON_VARIANTS = {
  default: 'primary',
  destructive: 'destructive',
  outline: 'outline',
  secondary: 'secondary',
  ghost: 'ghost',
  link: 'primary',
} as const;

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  /** Visual emphasis of the confirm action. Defaults to 'destructive' — confirm
   *  dialogs guard irreversible actions (per design.md §14: use AlertDialog for
   *  all destructive confirmations). */
  confirmVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  isLoading?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  confirmVariant = 'destructive',
  isLoading = false,
}) => {
  // AlertDialogAction defaults to destructive styling; honour a caller-supplied
  // confirmVariant by overriding with the matching button style (cn is
  // last-wins, so this className takes precedence).
  const actionClassName = buttonStyles({ variant: BUTTON_VARIANTS[confirmVariant] });

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>{cancelButtonText}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isLoading}
            className={actionClassName}
          >
            {confirmButtonText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
